import { ClientError } from "graphql-request";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import useConnectState from "~/lib/client/hooks/useConnectionState";
import { useGraphqlClientWithAuth } from "~/lib/client/hooks/useGraphqlClient";
import {
  GiveFanInput,
  GiveFanPayload,
  GiveRadioFanMutation,
  RadioAdminContentQuery,
  RadioChannel,
  RadioFormContentQuery,
} from "~/lib/graphql/client";
import { useAppContext } from "~/pages/_app";

export const addRadioResponseInputSchema = z.object({
  radioSegmentId: z.string().transform((value) => parseInt(value, 10)),
  content: z.string(),
  userMediumReferenceIds: z.optional(z.array(z.number())),
});

export type AddRadioResponseInput = z.infer<typeof addRadioResponseInputSchema>;

export function useAddRadioResponse() {
  const client = useGraphqlClientWithAuth();
  const { state, setConnecting, setError, setData, setReady } =
    useConnectState();

  const createRadioResponse = useCallback(
    async (input: AddRadioResponseInput): Promise<boolean> => {
      if (!client) return false;
      setConnecting();

      return await client
        .addRadioResponse({ input })
        .then((response) => {
          setData(response);
          return true;
        })
        .catch((error) => {
          console.error("Failed to add radio response");
          if (error instanceof ClientError) {
            setError(error);
          } else {
            throw error;
          }

          return false;
        });
    },
    [client]
  );

  useEffect(() => {
    if (client) {
      setReady();
    }
  }, [client]);

  return { addRadioResponse: createRadioResponse, state };
}

type SelectOption = { label: string; value: string };

export const EMPTY_OPTION_VALUE = "-1";

const emptyOption: SelectOption = {
  value: EMPTY_OPTION_VALUE,
  label: "コーナーを選択してください",
};

export const options = (
  radioSegments: RadioFormContentQuery["radioChannel"]["radioSegments"],
  channelId?: number
) => {
  // チャンネルIDが1の場合、特定のカテゴリーを除外
  const filteredSegments =
    channelId === 1
      ? radioSegments.filter((segment) => ![4, 9, 10, 11].includes(segment.id))
      : radioSegments;

  return [
    emptyOption,
    ...filteredSegments.map((segment) => ({
      value: segment.id.toString(),
      label: segment.name,
    })),
  ];
};

// 3600x1890
// => 400x210
// => 200x105
// => 40x21
// => 1600x840
export const BANNER_IMAGE_URL =
  "https://d1stpor4o81wuq.cloudfront.net/images/mail_karatachi_baner.jpg";

export function useIsEligibleRadioForm(channelId?: number) {
  const { currentUser } = useAppContext();

  // チャンネルIDごとに許可するユーザーIDを定義
  const allowedUsersByChannel: Record<number, number[]> = {
    1: [1, 2, 3, 2388, 177],
    2: [1, 2, 3, 2388, 2447],
  };

  if (!channelId) {
    return [1, 2, 3, 2388].includes(currentUser?.id ?? 0);
  }

  const allowedUsers = allowedUsersByChannel[channelId] || [];
  return allowedUsers.includes(currentUser?.id ?? 0);
}

export function useRadioAdminPageContent(channelId: number) {
  const client = useGraphqlClientWithAuth();
  const [content, setContent] = useState<RadioAdminContentQuery | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!client) return;

    try {
      const result = await client.radioAdminContent({ channelId });
      if (!result?.radioChannel) {
        throw new Error("チャンネルが見つかりません");
      }
      setContent(result);
      setError(null);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err : new Error("Unknown error"));
      setContent(null);
    }
  }, [client, channelId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { content, error, fetch };
}

export function useRadioChannels() {
  const client = useGraphqlClientWithAuth();
  const [channels, setChannels] = useState<RadioChannel[]>([]);

  const fetch = useCallback(() => {
    if (!client) return;

    client
      .radioChannels()
      .then((result) => {
        if (result && result.radioChannels) {
          setChannels(result.radioChannels as unknown as RadioChannel[]);
        }
      })
      .catch(console.error);
  }, [client]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { channels, fetch };
}

const addRadioSegmentInputSchema = z.object({
  radioChannelId: z.string().transform((value) => parseInt(value, 10)),
  name: z.string(),
});

export type AddRadioSegmentInput = z.infer<typeof addRadioSegmentInputSchema>;

export function useAddRadioSegment() {
  const client = useGraphqlClientWithAuth();
  const { state, setConnecting, setError, setData, setReady } =
    useConnectState();

  const addRadioSegment = useCallback(
    async (input: AddRadioSegmentInput): Promise<boolean> => {
      if (!client) return false;
      const parsedInput = addRadioSegmentInputSchema.safeParse(input);

      if (!parsedInput.success) {
        console.error("Failed to parse input");
        return false;
      }

      setConnecting();

      return await client
        .addRadioSegment({ input: parsedInput.data })
        .then((response) => {
          setData(response);
          return true;
        })
        .catch((error) => {
          console.error("Failed to add radio segment");
          if (error instanceof ClientError) {
            setError(error);
          } else {
            throw error;
          }

          return false;
        });
    },
    [client]
  );

  useEffect(() => {
    if (client) {
      setReady();
    }
  }, [client]);

  return { addRadioSegment, state };
}

const adoptRadioResponseInputSchema = z.object({
  radioResponseId: z.number(),
});

export type AdoptRadioResponseInput = z.infer<
  typeof adoptRadioResponseInputSchema
>;

export function useAdoptRadioResponse() {
  const client = useGraphqlClientWithAuth();
  const { state, setConnecting, setError, setData, setReady } =
    useConnectState();

  const adoptRadioResponse = useCallback(
    async (input: AdoptRadioResponseInput): Promise<boolean> => {
      if (!client) return false;

      setConnecting();

      return await client
        .adoptRadioResponse({ input })
        .then((response) => {
          setData(response);
          return true;
        })
        .catch((error) => {
          console.error("Failed to adopt radio response");
          if (error instanceof ClientError) {
            setError(error);
          } else {
            throw error;
          }

          return false;
        });
    },
    [client]
  );

  useEffect(() => {
    if (client) {
      setReady();
    }
  }, [client]);

  return { adoptRadioResponse, state };
}

export function useRejectAdoption() {
  const client = useGraphqlClientWithAuth();
  const { state, setConnecting, setError, setData, setReady } =
    useConnectState();

  const rejectAdoption = useCallback(
    async (input: AdoptRadioResponseInput): Promise<boolean> => {
      if (!client) return false;

      setConnecting();

      return await client
        .rejectRadioResponse({ input })
        .then((response) => {
          setData(response);
          return true;
        })
        .catch((error) => {
          console.error("Failed to reject adoption");
          if (error instanceof ClientError) {
            setError(error);
          } else {
            throw error;
          }

          return false;
        });
    },
    [client]
  );

  useEffect(() => {
    if (client) {
      setReady();
    }
  }, [client]);

  return { rejectAdoption, state };
}

export function useKeepRadioResponse() {
  const client = useGraphqlClientWithAuth();
  const { state, setConnecting, setError, setData, setReady } =
    useConnectState();

  const keepRadioResponse = useCallback(
    async (input: AdoptRadioResponseInput): Promise<boolean> => {
      if (!client) return false;

      setConnecting();

      return await client
        .keepRadioResponse({ input })
        .then((response) => {
          setData(response);
          return true;
        })
        .catch((error) => {
          console.error("Failed to keep radio response");
          if (error instanceof ClientError) {
            setError(error);
          } else {
            throw error;
          }

          return false;
        });
    },
    [client]
  );

  useEffect(() => {
    if (client) {
      setReady();
    }
  }, [client]);

  return { keepRadioResponse, state };
}

export function useUnKeepRadioResponse() {
  const client = useGraphqlClientWithAuth();
  const { state, setConnecting, setError, setData, setReady } =
    useConnectState();

  const unKeepRadioResponse = useCallback(
    async (input: AdoptRadioResponseInput): Promise<boolean> => {
      if (!client) return false;

      setConnecting();

      return await client
        .unKeepRadioResponse({ input })
        .then((response) => {
          setData(response);
          return true;
        })
        .catch((error) => {
          console.error("Failed to unkeep radio response");
          if (error instanceof ClientError) {
            setError(error);
          } else {
            throw error;
          }

          return false;
        });
    },
    [client]
  );

  useEffect(() => {
    if (client) {
      setReady();
    }
  }, [client]);

  return { unKeepRadioResponse, state };
}

export function useGiveRadioFan() {
  const client = useGraphqlClientWithAuth();
  const { state, setConnecting, setError, setData, setReady } =
    useConnectState<GiveRadioFanMutation>();

  const giveRadioFan = useCallback(
    async (input: GiveFanInput): Promise<boolean> => {
      if (!client) return false;

      setConnecting();

      return await client
        .giveRadioFan({ input })
        .then((response) => {
          setData(response);
          return true;
        })
        .catch((error) => {
          console.error("Failed to give radio fan");
          if (error instanceof ClientError) {
            setError(error);
          } else {
            throw error;
          }

          return false;
        });
    },
    [client]
  );

  const setToReady = useCallback(() => {
    if (client) {
      setReady();
    }
  }, [client]);

  useEffect(() => {
    if (client) {
      setReady();
    }
  }, [client]);

  return { giveRadioFan, state, setReady: setToReady };
}
